import { Badge } from '@/platform/ui';
import useDebounce from '@/react/hooks/useDebounce';
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { usePage } from '@inertiajs/react';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

const MIN_CHARACTERS = 3;

const isInactive = status => [-1, -2].includes(status);

const CustomerOption = ({ customer }) => (
    <ComboboxOption
        // TODO Change to Inertia Link when customers page is migrated
        as='a'
        value={customer}
        href={`/customers/${customer.id}/info`}
        className='group flex cursor-pointer flex-col gap-1 px-3 py-2 data-focus:bg-gray-100'
    >
        <span className='flex items-center gap-2'>
            {isInactive(customer.status) ? (
                <XCircleIcon className='size-4 shrink-0 text-red-500' />
            ) : (
                <CheckCircleIcon className='size-4 shrink-0 text-green-500' />
            )}
            <span className='truncate font-medium text-gray-900'>
                {customer.name}
            </span>
        </span>

        <span className='block pl-6 text-xs text-gray-500'>
            <span>
                {customer.customer_number}
                {customer.reseller?.name && ` | ${customer.reseller.name}`}
            </span>

            <span className='mt-1 flex items-end gap-1'>
                {customer.has_analyse && (
                    <Badge color='blue' size='sm'>
                        Analyse
                    </Badge>
                )}
                {customer.has_zekerheid && (
                    <Badge color='orange' size='sm'>
                        Zekerheid
                    </Badge>
                )}
                {customer.has_legal && (
                    <Badge color='green' size='sm'>
                        Legal
                    </Badge>
                )}
                {customer.has_whk_controle && (
                    <Badge color='gray' size='sm'>
                        Whk Controle
                    </Badge>
                )}
            </span>
        </span>
    </ComboboxOption>
);

const CustomerSearch = () => {
    const { auth } = usePage().props;

    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query);

    const [data, setData] = useState(null);
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        if (debouncedQuery.length < MIN_CHARACTERS) {
            return;
        }

        const controller = new AbortController();

        setIsFetching(true);

        axios
            .get('/api/customers/fetch-topbar', {
                params: { search: debouncedQuery },
                signal: controller.signal,
            })
            .then(response => {
                setData(response);
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    setIsFetching(false);
                }
            })
            .finally(() => setIsFetching(false));

        return () => controller.abort();
    }, [debouncedQuery]);

    const showOptions = useMemo(
        () => debouncedQuery.length >= MIN_CHARACTERS,
        [debouncedQuery]
    );

    return (
        <Combobox
            immediate
            onClose={() => setQuery('')}
            onChange={() => setQuery('')}
        >
            <div className='relative w-64'>
                <div className='relative'>
                    <ComboboxInput
                        autoComplete='off'
                        spellCheck={false}
                        displayValue={() => query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder={
                            auth?.customer
                                ? `${auth.customer.number} | ${auth.customer.name}`
                                : 'Klant zoeken'
                        }
                        className='w-full border-0 bg-white py-1.5 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none'
                    />
                </div>

                {showOptions && (
                    <ComboboxOptions
                        static
                        className='absolute z-50 mt-1 max-h-96 min-w-[var(--input-width)] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg'
                        modal={false}
                    >
                        {isFetching && data?.data?.length === 0 && (
                            <div className='px-3 py-2 text-sm text-gray-500'>
                                Bezig met zoeken...
                            </div>
                        )}

                        {!isFetching && data?.data?.length === 0 && (
                            <div className='px-3 py-2 text-sm text-gray-500'>
                                Geen resultaten gevonden.
                            </div>
                        )}

                        {data?.data?.map(customer => (
                            <CustomerOption
                                key={customer.id}
                                customer={customer}
                            />
                        ))}
                    </ComboboxOptions>
                )}
            </div>
        </Combobox>
    );
};

export default CustomerSearch;
